import { useCallback, useEffect, useRef, useState } from 'react';
import { BraindayAPI, BraindayError } from '@/services/BraindayAPI';
import { useTaskStore } from '@/stores/taskStore';
import { useAskThreadStore } from '@/stores/askThreadStore';
import { toDateKey } from '@/lib/dateKey';
import { selectAiTaskContext } from './selectAiTaskContext';
import { createStreamParser } from './streamParser';
import type { AiAction } from '@/types';

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
  actions?: AiAction[];
}

const HISTORY_LIMIT = 8;

function timeOfDay(d: Date): 'morning' | 'afternoon' | 'evening' | 'night' {
  const h = d.getHours();
  if (h < 5) return 'night';
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  if (h < 21) return 'evening';
  return 'night';
}

/** Compact, model-readable summary of an assistant turn that includes any
 * proposed actions, so follow-ups ("make it 3pm") resolve even when the user
 * hasn't tapped "Do it" yet (the task isn't in the store). */
function historyContent(turn: ChatTurn): string {
  if (turn.role !== 'assistant' || !turn.actions || turn.actions.length === 0) return turn.content;
  const proposals = turn.actions.map((a) => ` [proposed ${a.type}: ${a.label}]`).join('');
  return turn.content + proposals;
}

export function useCommand() {
  // Reactive subscription — picks up async (native) hydration automatically,
  // exactly like the other persisted stores in the app.
  const messages = useAskThreadStore((s) => s.messages);
  const append = useAskThreadStore((s) => s.append);
  const clearThread = useAskThreadStore((s) => s.clear);
  const outcomes = useAskThreadStore((s) => s.outcomes);
  const markOutcome = useAskThreadStore((s) => s.markOutcome);
  const clearOutcome = useAskThreadStore((s) => s.clearOutcome);

  const [streamingText, setStreamingText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<BraindayError | null>(null);
  const callIdRef = useRef(0);
  const ctrlRef = useRef<AbortController | null>(null);

  // DEV-only screenshot seam + stale-day safety on mount.
  useEffect(() => {
    const store = useAskThreadStore.getState();
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      const seed = (window as { __BD_SEED_COMMAND_THREAD?: unknown }).__BD_SEED_COMMAND_THREAD;
      if (Array.isArray(seed) && store.messages.length === 0) {
        store.setThread(seed as ChatTurn[]);
        return;
      }
    }
    if (store.dayKey !== toDateKey(new Date()) && store.messages.length > 0) store.clear();
  }, []);

  useEffect(() => () => ctrlRef.current?.abort(), []);

  const run = useCallback(
    async (userMessage: string) => {
      ctrlRef.current?.abort();
      const ctrl = new AbortController();
      ctrlRef.current = ctrl;
      const myId = ++callIdRef.current;

      const now = new Date();
      const localDate = toDateKey(now);
      const tasks = selectAiTaskContext(useTaskStore.getState().tasks, now.getTime());
      const history = useAskThreadStore
        .getState()
        .messages.slice(-HISTORY_LIMIT)
        .map((m) => ({ role: m.role, content: historyContent(m) }));

      append({ role: 'user', content: userMessage });
      setStreamingText('');
      setError(null);
      setLoading(true);
      const parser = createStreamParser();

      try {
        const final = await BraindayAPI.command(
          {
            userMessage,
            context: {
              currentDate: localDate,
              timeOfDay: timeOfDay(now),
              nowMs: now.getTime(),
              tzOffsetMinutes: now.getTimezoneOffset(),
            },
            history,
            tasks,
          },
          (chunk) => {
            if (callIdRef.current !== myId) return;
            setStreamingText(parser.push(chunk).text);
          },
          { signal: ctrl.signal },
        );
        if (callIdRef.current === myId) {
          append({ role: 'assistant', content: final.text, actions: final.actions });
          setStreamingText('');
          setLoading(false);
        }
      } catch (e) {
        const err = e instanceof BraindayError ? e : new BraindayError('unknown', String(e));
        if (callIdRef.current === myId) {
          setError(err);
          setStreamingText('');
          setLoading(false);
        }
      }
    },
    [append],
  );

  /** Clear the thread (and the persisted store) and abort any in-flight stream. */
  const newChat = useCallback(() => {
    ctrlRef.current?.abort();
    callIdRef.current++;
    clearThread();
    setStreamingText('');
    setLoading(false);
    setError(null);
  }, [clearThread]);

  return {
    messages,
    streamingText,
    loading,
    error,
    run,
    newChat,
    outcomes,
    markOutcome,
    clearOutcome,
  };
}
